import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  activateStandaloneCharges,
  getStandaloneChargeActivationStatus,
  retryStandaloneChargeBiometry,
  StandaloneChargeActivationStateEnum,
  StandaloneChargeActivationStatusDTO
} from '../../../services/standalone-charges';
import { dangerNotification, successNotification } from '../../../services/notification';
import { AddressService } from '../../../services/address';
import { maskCpfOrCnpj, maskPhoneNumber, maskZipCode } from '../../../services/masks';
import { toAbsoluteUrl } from '../../../utils/toAbsoluteUrl';
import { getEstate } from '../../../services/estate';
import { EstateForGetEstatesResponseDTO } from '../../../dtos/estate/estate-for-get-estates-response.dto';

import { initialActivationForm } from '../constants';
import { ActivationFormState, ActivationStep, UpdateActivationFormFieldFn } from '../types';
import { applyRecipientTypeChange } from '../utils/applyRecipientTypeChange';
import {
  getNextActivationStep,
  getPreviousActivationStep
} from '../utils/activationStepNavigation';
import {
  normalizePagarmeBankHolderName,
  PAGARME_BANK_HOLDER_NAME_MAX_LENGTH
} from '../utils/activationFormHelpers';
import { buildPagarmeActivationPayload } from '../utils/buildPagarmeActivationPayload';
import {
  getLastActivationStep,
  validateActivationStep
} from '../utils/validateActivationStep';
import { mapActivationPayloadToForm } from '../utils/mapActivationPayloadToForm';
import {
  clearActivationDraft,
  hasActivationDraft,
  readActivationDraft,
  writeActivationDraft
} from '../utils/activationDraftStorage';

export { normalizePagarmeBankHolderName, PAGARME_BANK_HOLDER_NAME_MAX_LENGTH };

const getActivationPrefillFromEstate = (
  estate: EstateForGetEstatesResponseDTO
): Partial<ActivationFormState> => {
  const cnpj = estate.cnpj ? maskCpfOrCnpj(estate.cnpj) : '';
  const partnerDocument = estate.responsible_document
    ? maskCpfOrCnpj(estate.responsible_document)
    : '';
  const zipCode = estate.estate_zip_code ? maskZipCode(estate.estate_zip_code) : '';
  const bankHolderName = normalizePagarmeBankHolderName(
    estate.company_name || estate.name || ''
  );
  const normalizedState = (estate.estate_state || '').trim().toUpperCase();
  const companyPhone = estate.messaging_phone
    ? maskPhoneNumber(estate.messaging_phone)
    : '';
  const partnerPhone = estate.responsible_phone
    ? maskPhoneNumber(estate.responsible_phone)
    : '';

  return {
    recipientType: 'corporation',
    email: (estate.email || '').trim(),
    document: cnpj,
    phone: companyPhone,
    companyName: (estate.company_name || '').trim(),
    tradingName: (estate.name || '').trim(),
    partnerName: (estate.responsible_name || '').trim(),
    partnerEmail: (estate.responsible_email || '').trim(),
    partnerDocument,
    partnerPhone,
    zipCode,
    street: (estate.estate_street || '').trim(),
    streetNumber: (estate.estate_number || '').trim(),
    neighborhood: (estate.estate_district || '').trim(),
    city: (estate.estate_city || '').trim(),
    state: normalizedState,
    complementary: (estate.estate_complement || '').trim(),
    bankHolderName,
    bankHolderType: 'company',
    bankHolderDocument: cnpj,
    bankCode: (estate.bank_account?.bank_code || '').trim(),
    bankBranchNumber: (estate.bank_account?.agency_number || '').trim(),
    bankBranchCheckDigit: (estate.bank_account?.agency_dv || '').trim(),
    bankAccountNumber: (estate.bank_account?.account_number || '').trim(),
    bankAccountCheckDigit: (estate.bank_account?.account_dv || '').trim()
  };
};

const hasSubmittedActivation = (
  activation: StandaloneChargeActivationStatusDTO['activation'] | undefined
): boolean =>
  Boolean(
    activation &&
    activation.status !== StandaloneChargeActivationStateEnum.notStarted
  );

export const buildQrCodeImageUrl = (value?: string): string => {
  const payload = String(value || '').trim();
  if (!payload) return '';
  if (payload.startsWith('data:image/')) return payload;
  if (payload.startsWith('http://') || payload.startsWith('https://')) return payload;
  return `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(
    payload
  )}`;
};

type Params = {
  needsActivationGate: boolean;
  enableActivationManagement: boolean;
  activationEstateId: string;
  appliedFilters: unknown;
  onEnabled: () => void;
};

export const useStandaloneChargesActivation = ({
  needsActivationGate,
  enableActivationManagement,
  activationEstateId,
  onEnabled
}: Params) => {
  const [activationStatus, setActivationStatus] =
    useState<StandaloneChargeActivationStatusDTO | null>(null);
  const [activationStatusError, setActivationStatusError] = useState(false);
  const [activationLoading, setActivationLoading] = useState(false);
  const [activationSaving, setActivationSaving] = useState(false);
  const [retryBiometrySaving, setRetryBiometrySaving] = useState(false);
  const [retryBiometryModalOpen, setRetryBiometryModalOpen] = useState(false);
  const [activationForm, setActivationForm] =
    useState<ActivationFormState>(initialActivationForm);
  const [activationWizardOpen, setActivationWizardOpen] = useState(false);
  const [activationStep, setActivationStep] = useState<ActivationStep>(1);
  const [activationStepError, setActivationStepError] = useState('');
  const [addressLookupLoading, setAddressLookupLoading] = useState(false);
  const [addressLookupError, setAddressLookupError] = useState<string | null>(null);
  const [addressAutoFilled, setAddressAutoFilled] = useState<string[]>([]);
  const [wizardEstateId, setWizardEstateId] = useState('');

  const validationOptions = useMemo(() => ({ mode: 'estate' as const }), []);
  const effectiveWizardEstateId = wizardEstateId || activationEstateId;
  const estateDraftScope = useMemo(
    () => ({ type: 'estate' as const, estateId: effectiveWizardEstateId }),
    [effectiveWizardEstateId]
  );

  const canCreateCharges = !needsActivationGate || Boolean(activationStatus?.standaloneChargesEnabled);
  const isFeatureEnabled = Boolean(activationStatus?.featureEnabled);
  const activationIsPendingKyc =
    activationStatus?.activation?.status === StandaloneChargeActivationStateEnum.pagarmePending;
  const activationIsBiometryRejected =
    activationStatus?.activation?.status ===
      StandaloneChargeActivationStateEnum.biometryRejected ||
    Boolean(activationStatus?.activation?.biometryRejected);
  const activationIsSupportRequired =
    activationStatus?.activation?.status ===
      StandaloneChargeActivationStateEnum.supportRequired ||
    Boolean(activationStatus?.activation?.supportRequired);
  const canRetryBiometry = Boolean(activationStatus?.activation?.canRetryBiometry);
  const activationBiometryMeta = activationStatus?.activation;
  const activationKycLink = activationStatus?.activation?.kycLinkUrl || '';
  const activationKycToken = activationStatus?.activation?.kycToken || '';
  const activationKycPageUrl = activationKycToken
    ? toAbsoluteUrl(`/biometria/${activationKycToken}`)
    : '';
  const activationKycQrPayload =
    activationStatus?.activation?.kycQrPayload ||
    activationStatus?.activation?.kycLinkUrl ||
    '';
  const activationKycQrImageUrl = buildQrCodeImageUrl(activationKycQrPayload);

  const hasLocalDraft = activationEstateId
    ? hasActivationDraft(estateDraftScope)
    : false;

  const activationStatusValue = activationStatus?.activation?.status;
  const canStartActivation =
    !activationStatusValue ||
    activationStatusValue === StandaloneChargeActivationStateEnum.notStarted ||
    activationStatusValue === StandaloneChargeActivationStateEnum.pagarmeRejected;
  const canEditPendingActivation = activationIsPendingKyc;
  const showActivationAction =
    isFeatureEnabled && (canStartActivation || canEditPendingActivation);

  const activationActionLabel = useMemo(() => {
    if (canEditPendingActivation) return 'Visualizar/editar cadastro';
    if (hasLocalDraft) return 'Continuar cadastro';
    return 'Iniciar ativação';
  }, [canEditPendingActivation, hasLocalDraft]);

  const loadActivationStatus = useCallback(async () => {
    if (!enableActivationManagement || !activationEstateId) return;
    setActivationLoading(true);
    try {
      const status = await getStandaloneChargeActivationStatus(activationEstateId);
      setActivationStatus(status);
      setActivationStatusError(false);
    } catch (error) {
      setActivationStatus({
        estateId: activationEstateId,
        featureEnabled: false,
        standaloneChargesEnabled: false,
        activation: null
      });
      setActivationStatusError(true);
    } finally {
      setActivationLoading(false);
    }
  }, [activationEstateId, enableActivationManagement]);

  useEffect(() => {
    loadActivationStatus();
  }, [loadActivationStatus]);

  useEffect(() => {
    if (!activationWizardOpen || !effectiveWizardEstateId) return;
    if (hasSubmittedActivation(activationStatus?.activation || null)) return;

    writeActivationDraft(estateDraftScope, {
      step: activationStep,
      form: activationForm
    });
  }, [
    activationWizardOpen,
    effectiveWizardEstateId,
    activationStep,
    activationForm,
    activationStatus?.activation,
    estateDraftScope
  ]);

  const updateActivationFormField: UpdateActivationFormFieldFn = (field, value) => {
    setActivationForm(prev => {
      if (field !== 'recipientType') return { ...prev, [field]: value };
      return applyRecipientTypeChange(
        prev,
        value as ActivationFormState['recipientType']
      );
    });
  };

  const handleNextActivationStep = () => {
    const validationError = validateActivationStep(
      activationStep,
      activationForm,
      validationOptions
    );
    if (validationError) {
      setActivationStepError(validationError);
      return;
    }

    setActivationStepError('');
    const nextStep = getNextActivationStep(activationStep, activationForm.recipientType);
    if (nextStep) {
      setActivationStep(nextStep);
    }
  };

  const closeActivationWizard = () => {
    setActivationWizardOpen(false);
    setActivationStepError('');
    setAddressLookupError(null);
    setWizardEstateId('');
  };

  const handleBackActivationStep = () => {
    setActivationStepError('');
    if (activationStep <= 1) {
      closeActivationWizard();
      return;
    }

    const previousStep = getPreviousActivationStep(
      activationStep,
      activationForm.recipientType
    );
    if (previousStep) {
      setActivationStep(previousStep);
    }
  };

  const handleZipCodeLookup = async (zipCodeRaw: string) => {
    const cleanZip = zipCodeRaw.replace(/\D/g, '');
    if (cleanZip.length !== 8) return;

    setAddressLookupLoading(true);
    setAddressLookupError(null);
    setAddressAutoFilled([]);
    try {
      const response = await AddressService.getAddressByZipCode(cleanZip);
      const filled: string[] = [];

      if (response.street) {
        updateActivationFormField('street', response.street);
        filled.push('street');
      }
      if ((response as unknown as { district?: string }).district) {
        updateActivationFormField(
          'neighborhood',
          (response as unknown as { district?: string }).district || ''
        );
        filled.push('neighborhood');
      }
      if (response.city) {
        updateActivationFormField('city', response.city);
        filled.push('city');
      }
      if (response.state) {
        updateActivationFormField('state', response.state);
        filled.push('state');
      }

      setAddressAutoFilled(filled);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e || '');
      setAddressLookupError(msg || 'CEP não encontrado. Verifique e tente novamente.');
    } finally {
      setAddressLookupLoading(false);
    }
  };

  const openActivationWizard = useCallback(async (estateIdOverride?: string) => {
    const estateId = estateIdOverride || activationEstateId;
    if (!estateId) {
      dangerNotification(
        'Atenção',
        'Selecione uma imobiliária para editar o cadastro.'
      );
      return;
    }

    setWizardEstateId(estateId);
    setActivationStep(1);
    setActivationStepError('');
    setAddressLookupError(null);
    setAddressAutoFilled([]);

    let status = activationStatus;
    if (!status || status.estateId !== estateId) {
      try {
        status = await getStandaloneChargeActivationStatus(estateId);
        setActivationStatus(status);
        setActivationStatusError(false);
      } catch (error) {
        setActivationStatus({
          estateId,
          featureEnabled: false,
          standaloneChargesEnabled: false,
          activation: null
        });
        setActivationStatusError(true);
        setWizardEstateId('');
        dangerNotification(
          'Erro',
          'Não foi possível carregar o cadastro da imobiliária.'
        );
        return;
      }
    }

    if (!status?.featureEnabled) {
      setWizardEstateId('');
      dangerNotification(
        'Cobranças não habilitadas',
        'Habilite o envio de cobranças na edição da imobiliária antes de iniciar a ativação.'
      );
      return;
    }

    let nextForm: ActivationFormState = { ...initialActivationForm };

    try {
      const estate = await getEstate(estateId);
      nextForm = {
        ...nextForm,
        ...getActivationPrefillFromEstate(estate)
      };
    } catch (error) {
      dangerNotification(
        'Atenção',
        'Não foi possível pré-preencher os dados da imobiliária. Você pode seguir preenchendo manualmente.'
      );
    }

    const activation = status?.activation;
    let nextStep: ActivationStep = 1;
    const draftScope = { type: 'estate' as const, estateId };

    if (hasSubmittedActivation(activation)) {
      nextForm = mapActivationPayloadToForm(
        {
          recipientType: activation?.recipientType,
          registerInformation: activation?.registerInformation,
          defaultBankAccount: activation?.defaultBankAccount,
          payload: activation?.payload
        },
        nextForm
      );
    } else {
      const draft = readActivationDraft<ActivationFormState>(draftScope);
      if (draft) {
        nextForm = { ...nextForm, ...draft.form };
        nextStep = draft.step;
      }
    }

    setActivationForm(nextForm);
    setActivationStep(nextStep);
    setActivationWizardOpen(true);
  }, [activationEstateId, activationStatus]);

  const handleActivate = async () => {
    const lastStep = getLastActivationStep();
    for (let step = 1; step <= lastStep; step += 1) {
      const validationError = validateActivationStep(
        step as ActivationStep,
        activationForm,
        validationOptions
      );
      if (validationError) {
        setActivationStep(step as ActivationStep);
        setActivationStepError(validationError);
        return;
      }
    }

    setActivationSaving(true);
    try {
      const payload = buildPagarmeActivationPayload(activationForm);
      const estateId = wizardEstateId || activationEstateId;
      if (estateId) {
        payload.estateId = estateId;
      }
      const status = await activateStandaloneCharges(payload);
      setActivationStatus(status);
      clearActivationDraft({ type: 'estate', estateId });

      const isEnabled = Boolean(status?.standaloneChargesEnabled);
      const isPendingKyc =
        status?.activation?.status === StandaloneChargeActivationStateEnum.pagarmePending;

      successNotification(
        'Sucesso',
        isEnabled
          ? 'Ativação concluída com sucesso!'
          : isPendingKyc
            ? 'Cadastro atualizado. Conclua a biometria para liberar emissão de cobranças.'
            : 'Cadastro enviado. Conclua a biometria para liberar emissão de cobranças.'
      );
      closeActivationWizard();
      setActivationStep(1);
      onEnabled();
      if (!isEnabled) {
        await loadActivationStatus();
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      dangerNotification(
        'Erro',
        message || 'Não foi possível concluir a ativação da imobiliária.'
      );
    } finally {
      setActivationSaving(false);
    }
  };

  const openRetryBiometryModal = () => setRetryBiometryModalOpen(true);
  const closeRetryBiometryModal = () => setRetryBiometryModalOpen(false);

  const handleRetryBiometry = async () => {
    setRetryBiometrySaving(true);
    try {
      const status = await retryStandaloneChargeBiometry(activationEstateId);
      setActivationStatus(status);
      setRetryBiometryModalOpen(false);
      successNotification(
        'Sucesso',
        'Novo cadastro criado. Conclua a biometria para liberar emissão de cobranças.'
      );
      await loadActivationStatus();
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      dangerNotification(
        'Erro',
        message || 'Não foi possível iniciar uma nova tentativa de biometria.'
      );
    } finally {
      setRetryBiometrySaving(false);
    }
  };

  return {
    activationStatus,
    activationStatusError,
    activationLoading,
    activationSaving,
    activationForm,
    setActivationForm,
    activationWizardOpen,
    activationStep,
    activationStepError,
    addressLookupLoading,
    addressLookupError,
    addressAutoFilled,
    canCreateCharges,
    isFeatureEnabled,
    activationIsPendingKyc,
    activationIsBiometryRejected,
    activationIsSupportRequired,
    canRetryBiometry,
    activationBiometryMeta,
    activationKycLink,
    activationKycToken,
    activationKycPageUrl,
    activationKycQrImageUrl,
    hasLocalDraft,
    canStartActivation,
    canEditPendingActivation,
    showActivationAction,
    activationActionLabel,
    loadActivationStatus,
    updateActivationFormField,
    openActivationWizard,
    closeActivationWizard,
    handleNextActivationStep,
    handleBackActivationStep,
    handleZipCodeLookup,
    handleActivate,
    retryBiometryModalOpen,
    retryBiometrySaving,
    openRetryBiometryModal,
    closeRetryBiometryModal,
    handleRetryBiometry
  };
};
